import React, { useMemo, useState, useEffect } from "react";
import Success from "../../../../formHelper/success";
import apiRequestCall from "../../../../apiHelper/globalHelper";
import { all_routes } from "../../../../router/backendComponents/admin/all_routes";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../core/data/redux/store";
type Id = string;
const uid = () => Math.random().toString(36).slice(2);

interface NamedAmount {
  id: Id;
  name: string;
  amount: number | "";
}

interface TravelFormState {
  id: any;
  expanses_company_detail_id: any;
  purpose_order_no?: string;
  purchaseEquipment: NamedAmount[];
  purchaseHardware: NamedAmount[];
  labor: NamedAmount[];
  purpose: string;
}

interface Permission {
  id: number;
  role_id: number;
  name: string;
  role: {
    permissions: Record<string, Record<string, string>>;
  };
}

interface Props {
  onNext?: () => void;
  onPrev?: () => void;
}

/** simple hook to detect mobile breakpoint */
const useIsMobile = (maxWidth = 767.98) => {
  const isClient = typeof window !== "undefined";
  const [isMobile, setIsMobile] = useState<boolean>(
    isClient ? window.innerWidth <= maxWidth : false
  );
  useEffect(() => {
    if (!isClient) return;
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const handler = (e: MediaQueryListEvent | MediaQueryList) =>
      setIsMobile("matches" in e ? e.matches : mq.matches);
    if (typeof mq.addEventListener === "function")
      mq.addEventListener("change", handler as any);
    else (mq as any).addListener(handler);
    setIsMobile(mq.matches);
    return () => {
      if (typeof mq.removeEventListener === "function")
        mq.removeEventListener("change", handler as any);
      else (mq as any).removeListener(handler);
    };
  }, [maxWidth, isClient]);
  return isMobile;
};

/** robust usePrefersDark */
const usePrefersDark = (): boolean => {
  const get = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;

  const [isDark, setIsDark] = useState<boolean>(get());

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (event: MediaQueryListEvent | MediaQueryList) => {
      const matches =
        typeof (event as any).matches === "boolean"
          ? (event as any).matches
          : mq.matches;
      setIsDark(matches);
    };
    if (typeof mq.addEventListener === "function")
      mq.addEventListener("change", handler as EventListener);
    else (mq as any).addListener(handler);
    setIsDark(mq.matches);
    return () => {
      if (typeof mq.removeEventListener === "function")
        mq.removeEventListener("change", handler as EventListener);
      else (mq as any).removeListener(handler);
    };
  }, []);

  return isDark;
};

export default function CustomerOrder({ onNext, onPrev }: Props): JSX.Element {
  const isMobile = useIsMobile();
  const isDark = usePrefersDark();
  const backendRoute = all_routes.backend;

  const [form, setForm] = useState<TravelFormState>({
    id: "",
    expanses_company_detail_id: "",
    purchaseEquipment: [{ id: uid(), name: "", amount: "" }],
    purchaseHardware: [{ id: uid(), name: "", amount: "" }],
    labor: [{ id: uid(), name: "", amount: "" }],
    purpose: "",
    purpose_order_no: "",
  });
  const permission = useSelector(
    (state: RootState) => state.rolesPermissions?.data
  ) as Permission | null;

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const profile = useSelector((s: RootState) => s.profile?.data);

  const addRow = <T extends { id: Id }>(list: T[], empty: T): T[] => [
    ...list,
    empty,
  ];
  const removeRow = <T extends { id: Id }>(list: T[], id: Id): T[] =>
    list.length > 1 ? list.filter((x) => x.id !== id) : list;

  const setList =
    <K extends keyof TravelFormState>(key: K) =>
    (
      updater: (
        list: NonNullable<TravelFormState[K]>
      ) => NonNullable<TravelFormState[K]>
    ) =>
      setForm((p) => {
        const current = (p[key] ?? []) as NonNullable<TravelFormState[K]>;
        return { ...p, [key]: updater(current) } as TravelFormState;
      });

  /* ---------- totals ---------- */
  const totals = useMemo(() => {
    const sum = (arr: { amount?: number | "" }[]) =>
      arr.reduce((acc, x) => acc + (Number(x.amount) || 0), 0);
    const equip = sum(form.purchaseEquipment);
    const hardware = sum(form.purchaseHardware);
    const labor = sum(form.labor);
    const grand = equip + hardware + labor;
    return { equip, hardware, labor, grand };
  }, [form]);

  /* ---------- validation ---------- */
  const sanitizeNamedAmounts = (arr: NamedAmount[]) =>
    arr
      .map((x) => ({
        id: x.id,
        name: (x.name ?? "").trim(),
        amount: x.amount === "" ? 0 : Number(x.amount || 0),
      }))
      .filter((x) => x.name !== "" || x.amount > 0);

  /* ---------- build payload ---------- */
  const buildPayload = () => {
    return {
      id: form.id ?? null,
      expanses_company_detail_id: form.expanses_company_detail_id ?? null,
      purpose_order_no: (form.purpose_order_no || "").trim() || null,
      purpose: form.purpose.trim() || null,
      purchaseEquipment: sanitizeNamedAmounts(form.purchaseEquipment).map(
        (x) => ({ name: x.name, amount: x.amount })
      ),
      purchaseHardware: sanitizeNamedAmounts(form.purchaseHardware).map(
        (x) => ({ name: x.name, amount: x.amount })
      ),
      labor: sanitizeNamedAmounts(form.labor).map((x) => ({
        name: x.name,
        amount: x.amount,
      })),
      totals,
    } as const;
  };

  /* ---------- populate form from server payload (accepts same shape) ---------- */
  const populateFromPayload = (payload: any) => {
    if (!payload) return;
    const p = payload?.data?.link_order ?? payload;
    setForm((prev) => ({
      ...prev,
      id: p?.id ?? "",
      expanses_company_detail_id: payload?.data?.id ?? "",
      purpose_order_no: p.purpose_order_no ?? "",
      purpose: p.purpose ?? "",
      purchaseEquipment:
        Array.isArray(p.purchase_equipment) && p.purchase_equipment.length
          ? p.purchase_equipment.map((x: any) => ({
              id: uid(),
              name: x.name ?? "",
              amount: x.amount == null ? "" : Number(x.amount),
            }))
          : [{ id: uid(), name: "", amount: "" }],
      purchaseHardware:
        Array.isArray(p.purchase_hardware) && p.purchase_hardware.length
          ? p.purchase_hardware.map((x: any) => ({
              id: uid(),
              name: x.name ?? "",
              amount: x.amount == null ? "" : Number(x.amount),
            }))
          : [{ id: uid(), name: "", amount: "" }],
      labor:
        Array.isArray(p.labor) && p.labor.length
          ? p.labor.map((x: any) => ({
              id: uid(),
              name: x.name ?? "",
              amount: x.amount == null ? "" : Number(x.amount),
            }))
          : [{ id: uid(), name: "", amount: "" }],
    }));
  };

  /* ---------- parse backend validation errors (Laravel style) ---------- */
  const parseErrors = (errorsObj: Record<string, string> = {}) => {
    const result: Record<string, any> = {};
    for (const key in errorsObj) {
      if (key.includes(".")) {
        const parts = key.split(".");
        if (parts.length >= 3) {
          const idx = parts[1];
          const field = parts.slice(2).join("_");
          result[`${parts[0]}_${idx}_${field}`] = errorsObj[key];
        } else {
          result[key] = errorsObj[key];
        }
      } else result[key] = errorsObj[key];
    }
    return result;
  };

  /* ---------- server calls ---------- */
  const tryLoadCmp = async () => {
    try {
      const res = await apiRequestCall("post", backendRoute.getExpansesDetails);
      if (res?.data) populateFromPayload(res.data);
    } catch (err) {
      console.error("Failed to load customer order details:", err);
    }
  };

  useEffect(() => {
    tryLoadCmp();
  }, []);

  const submit = async () => {
    setLoading(true);
    setErrors({});
    const payload = buildPayload();
    try {
      const res = await apiRequestCall(
        "post",
        backendRoute.addUpdateLinkOrder,
        {
          data: payload,
        }
      );

      if (res?.data?.code === 221) {
        setErrors(parseErrors(res?.data?.errors || {}));
        return;
      }

      if (res?.data) {
        populateFromPayload(res.data);
        tryLoadCmp();
        if (typeof setShowSuccess !== "undefined") setShowSuccess(true);
        if (typeof setShowMessage !== "undefined")
          setShowMessage(res?.data?.message ?? "Saved successfully");
      }
    } catch (err: any) {
      console.error("Save failed", err);
      alert("Save failed (see console)");
      setErrors({ server: err?.message ?? "Network error" });
    } finally {
      setLoading(false);
    }
  };

  /* ---------- rendering helpers (purchase rows) ---------- */
  const purchaseRows = (
    list: NamedAmount[],
    key: "purchaseEquipment" | "purchaseHardware" | "labor"
  ) =>
    list.map((r, i) =>
      isMobile ? (
        <div
          key={r.id}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <input
            className="form-control"
            placeholder={
              key === "labor"
                ? "Description (e.g., Helper name or role)"
                : "Item"
            }
            value={r.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setList(key)((l) =>
                l.map((x) =>
                  x.id === r.id ? { ...x, name: e.target.value } : x
                )
              )
            }
            style={{ minWidth: 0 }}
          />
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="number"
              className="form-control"
              placeholder="Amount"
              min={0}
              value={r.amount === "" ? "" : r.amount}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setList(key)((l) =>
                  l.map((x) =>
                    x.id === r.id
                      ? {
                          ...x,
                          amount:
                            e.target.value === "" ? "" : Number(e.target.value),
                        }
                      : x
                  )
                )
              }
              style={{ width: "100%", minWidth: 0 }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="btn btn-icon btn-sm btn-soft-danger"
                onClick={() => setList(key)((l) => removeRow(l, r.id))}
                disabled={list.length === 1}
                aria-label="remove"
              >
                <i className="feather-trash" />
              </button>
              {i === list.length - 1 && (
                <button
                  type="button"
                  className="btn btn-icon btn-sm btn-soft-info"
                  onClick={() =>
                    setList(key)((l) =>
                      addRow(l, { id: uid(), name: "", amount: "" })
                    )
                  }
                  aria-label="add"
                >
                  <i className="feather-plus" />
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div key={r.id} className="row g-2 align-items-center mb-2">
          <div className="col-7 col-sm-8" style={{ minWidth: 0 }}>
            <input
              className="form-control"
              placeholder={
                key === "labor"
                  ? "Description (e.g., Helper name or role)"
                  : "Item"
              }
              value={r.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setList(key)((l) =>
                  l.map((x) =>
                    x.id === r.id ? { ...x, name: e.target.value } : x
                  )
                )
              }
            />
          </div>
          <div className="col-3 col-sm-2" style={{ minWidth: 0 }}>
            <input
              type="number"
              className="form-control"
              placeholder="Amount"
              min={0}
              value={r.amount === "" ? "" : r.amount}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setList(key)((l) =>
                  l.map((x) =>
                    x.id === r.id
                      ? {
                          ...x,
                          amount:
                            e.target.value === "" ? "" : Number(e.target.value),
                        }
                      : x
                  )
                )
              }
            />
          </div>
          <div
            className="col-2 d-flex gap-2"
            style={{ justifyContent: "flex-end" }}
          >
            <button
              type="button"
              className="btn btn-icon btn-sm btn-soft-danger"
              onClick={() => setList(key)((l) => removeRow(l, r.id))}
              disabled={list.length === 1}
              aria-label="remove"
            >
              <i className="feather-trash" />
            </button>
            {i === list.length - 1 && (
              <button
                type="button"
                className="btn btn-icon btn-sm btn-soft-info"
                onClick={() =>
                  setList(key)((l) =>
                    addRow(l, { id: uid(), name: "", amount: "" })
                  )
                }
                aria-label="add"
              >
                <i className="feather-plus" />
              </button>
            )}
          </div>
        </div>
      )
    );

  const mobileCardStyle: React.CSSProperties = {
    border: isDark
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(0,0,0,0.06)",
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  };

  return (
    <div
      className="supplier-table-root mt-3"
      style={{ padding: isMobile ? 12 : 0 }}
    >
      {permission?.role?.permissions?.expances?.service_team && (
        <div
          className={isMobile ? "rt-card" : "mb-1"}
          style={isMobile ? { marginBottom: 12 } : undefined}
        >
          <small>
            <label style={{ display: "block", fontWeight: 600 }}>
              Purchase Order No. (if any)
            </label>
          </small>
          <input
            className="form-control"
            placeholder="Order no."
            value={form.purpose_order_no ?? ""}
            onChange={(e) =>
              setForm((p) => ({ ...p, purpose_order_no: e.target.value }))
            }
          />
        </div>
      )}

      {permission?.role?.permissions?.expances?.service_team && (
        <div
          className={isMobile ? "rt-card" : "mb-1"}
          style={isMobile ? { marginBottom: 12 } : undefined}
        >
          <small>
            <label style={{ display: "block", fontWeight: 600 }}>
              Purchase Equipment
            </label>
          </small>
          {purchaseRows(form.purchaseEquipment, "purchaseEquipment")}
        </div>
      )}

      {permission?.role?.permissions?.expances?.service_team && (
        <div
          className={isMobile ? "rt-card" : "mb-1"}
          style={isMobile ? { marginBottom: 12 } : undefined}
        >
          <small>
            <label style={{ display: "block", fontWeight: 600 }}>
              Purchase Hardware
            </label>
          </small>
          {purchaseRows(form.purchaseHardware, "purchaseHardware")}
        </div>
      )}

      {(permission?.role?.permissions?.expances?.service_team) && (
        <div
          className={isMobile ? "rt-card" : "mb-1"}
          style={isMobile ? { marginBottom: 12 } : undefined}
        >
          <small>
            <label style={{ display: "block", fontWeight: 600 }}>
              Labor Charges – No. of person & Amount{" "}
            </label>
          </small>
          {purchaseRows(form.labor, "labor")}
        </div>
      )}

      <div
        className={isMobile ? "rt-card" : "mb-1"}
        style={isMobile ? { marginBottom: 12 } : undefined}
      >
        <small>
          <label style={{ display: "block", fontWeight: 600 }}>
            Purpose of Visit / Details of visit{" "}
            <span className="text-danger">*</span>
          </label>
        </small>
        <textarea
          className="form-control"
          placeholder="Sales: purpose details. Service: installation/service details."
          style={{ minHeight: 100 }}
          value={form.purpose}
          onChange={(e) => setForm((p) => ({ ...p, purpose: e.target.value }))}
        />
        {errors["purpose"] && (
          <div className="text-danger small">{errors["purpose"]}</div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ color: "red", flex: 1, minWidth: 0 }}>
          {errors["server"] && (
            <div className="text-danger">{errors["server"]}</div>
          )}
        </div>

        <div
          style={{
            width: isMobile ? "100%" : 320,
            minWidth: 0,
            border: "1px solid var(--rt-border-color, rgba(0,0,0,0.06))",
            borderRadius: 6,
          }}
        >
          <div
            style={{
              border: "1px solid var(--rt-border-color)",
              borderRadius: 6,
              padding: 10,
              background: "var(--rt-card-bg)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Equipment</span>
              <strong>₹{totals.equip.toFixed(2)}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Hardware</span>
              <strong>₹{totals.hardware.toFixed(2)}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Labor</span>
              <strong>₹{totals.labor.toFixed(2)}</strong>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                borderTop: "1px solid var(--rt-border-color)",
                paddingTop: 8,
                marginTop: 8,
              }}
            >
              <h5>
                <span>Total</span>
              </h5>
              <strong>₹{totals.grand.toFixed(2)}</strong>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          justifyContent: "flex-end",
          marginTop: 12,
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <button
          type="button"
          className="btn btn-soft-warning"
          style={{ width: isMobile ? "100%" : undefined }}
          onClick={onPrev}
        >
          <i className="ti ti-arrow-left mb-1" /> back
        </button>
        <button
          type="button"
          className="btn btn-soft-success"
          onClick={submit}
          style={{ width: isMobile ? "100%" : undefined }}
          disabled={loading}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
              />
              Saving...
            </>
          ) : (
            <>
              Save & Next <i className="ti ti-arrow-right" />
            </>
          )}
        </button>
      </div>

      <Success
        id="OwnerSuccessModal"
        title="Link Order"
        message={showMessage}
        show={showSuccess}
        onClose={() => {
          setShowSuccess(false);
          try {
            onNext?.();
          } catch (err) {
            console.error("onNext callback failed", err);
          }
        }}
      />
    </div>
  );
}
